import { ActionNodeKind } from "./compound-action";

interface SkeletonNodeBase {
    readonly kind: string;
    readonly key: string;
}

export interface SkeletonPrimitiveActionNode extends SkeletonNodeBase {
    readonly kind: ActionNodeKind.Primitive;
    [key: string]: any;
}

export interface SkeletonFlowActionNode extends SkeletonNodeBase {
    readonly kind: ActionNodeKind.Flow;
    readonly actions: readonly SkeletonActionNode[];
    [key: string]: any;
}

export interface SkeletonConditionalFlowNode extends SkeletonNodeBase {
    readonly kind: ActionNodeKind.ConditionalFlow;
    readonly flow: SkeletonFlowActionNode;
    [key: string]: any;
}

export interface SkeletonConditionalActionNode extends SkeletonNodeBase {
    readonly kind: ActionNodeKind.Conditional;
    readonly conditionalFlows: readonly SkeletonConditionalFlowNode[];
    readonly elseFlow?: SkeletonFlowActionNode;
    [key: string]: any;
}

export interface SkeletonLoopNode extends SkeletonNodeBase {
    readonly kind: ActionNodeKind.Loop;
    readonly flow: SkeletonFlowActionNode;
    [key: string]: any;
}

export interface SkeletonAutomationRootNode extends SkeletonNodeBase {
    readonly kind: ActionNodeKind.AutomationRoot;
    readonly flow: SkeletonFlowActionNode;
    [key: string]: any;
}

export type SkeletonActionNode =
    | SkeletonPrimitiveActionNode
    | SkeletonFlowActionNode
    | SkeletonConditionalFlowNode
    | SkeletonConditionalActionNode
    | SkeletonLoopNode
    | SkeletonAutomationRootNode;

export interface SkeletonBuilderAction {
    readonly action: SkeletonActionNode;
    readonly appIDs?: readonly string[];
    title?: string;
    [key: string]: any;
}

export function isSkeletonActionNode(value: unknown): value is SkeletonActionNode {
    if (typeof value !== "object" || value === null) {
        return false;
    }

    const node = value as any;

    if (typeof node.kind !== "string" || typeof node.key !== "string") {
        return false;
    }

    // I think this check is overly strict and likely to become a bug bear
    switch (node.kind) {
        case ActionNodeKind.Primitive:
            return true;

        case ActionNodeKind.Flow:
            return Array.isArray(node.actions) && node.actions.every((action: unknown) => isSkeletonActionNode(action));

        case ActionNodeKind.ConditionalFlow:
            return node.flow !== undefined && isSkeletonActionNode(node.flow) && node.flow.kind === ActionNodeKind.Flow;

        case ActionNodeKind.Conditional:
            return (
                Array.isArray(node.conditionalFlows) &&
                node.conditionalFlows.every(
                    (cf: any) =>
                        cf.kind === ActionNodeKind.ConditionalFlow &&
                        cf.flow !== undefined &&
                        isSkeletonActionNode(cf.flow) &&
                        cf.flow.kind === ActionNodeKind.Flow
                ) &&
                (node.elseFlow === undefined ||
                    (isSkeletonActionNode(node.elseFlow) && node.elseFlow.kind === ActionNodeKind.Flow))
            );

        case ActionNodeKind.Loop:
            return node.flow !== undefined && isSkeletonActionNode(node.flow) && node.flow.kind === ActionNodeKind.Flow;

        case ActionNodeKind.AutomationRoot:
            return node.flow !== undefined && isSkeletonActionNode(node.flow) && node.flow.kind === ActionNodeKind.Flow;

        default:
            return false;
    }
}
